import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useTranslation } from "@/i18n/useTranslation";
import { useUpdateVersion } from "../hooks";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import type { ScriptVersion } from "../types";

interface EditVersionFormProps {
  version: ScriptVersion;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const EditVersionForm = ({
  version,
  onSuccess,
  onCancel,
}: EditVersionFormProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { mutateAsync: updateVersion, isPending } = useUpdateVersion();

  const [scriptUrl, setScriptUrl] = useState(version.script_url);
  const [versionName, setVersionName] = useState(version.version_name || "");
  const [notes, setNotes] = useState(version.notes || "");
  const [notesTh, setNotesTh] = useState(version.notes_th || "");
  const [passwordProtected, setPasswordProtected] = useState(version.password_protected);
  const [reviewPassword, setReviewPassword] = useState(version.review_password || "");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateVersion({
        versionId: version.id,
        scriptUrl: scriptUrl.trim(),
        versionName: versionName.trim() || undefined,
        notes: notes.trim() || undefined,
        notesTh: notesTh.trim() || undefined,
        passwordProtected,
        reviewPassword: passwordProtected ? reviewPassword : undefined,
      });

      toast({
        title: t("versions.version_updated"),
      });

      onSuccess?.();
    } catch (error) {
      console.error("Error updating version:", error);
      toast({
        title: "Error updating version",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="edit-script-url">{t("versions.script_url")}</Label>
        <Input
          id="edit-script-url"
          type="url"
          value={scriptUrl}
          onChange={(e) => setScriptUrl(e.target.value)}
          placeholder="https://..."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="edit-version-name">
          {t("versions.version_name")}
        </Label>
        <Input
          id="edit-version-name"
          value={versionName}
          onChange={(e) => setVersionName(e.target.value)}
          placeholder={t("versions.version_name_placeholder")}
          maxLength={100}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="edit-notes">
          {t("versions.notes")}
        </Label>
        <Textarea
          id="edit-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t("versions.notes_placeholder")}
          rows={3}
          maxLength={500}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="edit-notes-th">
          {t("versions.notes_th")}
        </Label>
        <Textarea
          id="edit-notes-th"
          value={notesTh}
          onChange={(e) => setNotesTh(e.target.value)}
          placeholder={t("versions.notes_th_placeholder")}
          rows={3}
          maxLength={500}
        />
      </div>

      <div className="space-y-3 rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="edit-password-protected">
              {t("versions.password_protection")}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t("versions.password_protection_hint")}
            </p>
          </div>
          <Switch
            id="edit-password-protected"
            checked={passwordProtected}
            onCheckedChange={setPasswordProtected}
          />
        </div>

        {passwordProtected && (
          <div className="space-y-2 pt-2">
            <Label htmlFor="edit-review-password">Password</Label>
            <Input
              id="edit-review-password"
              type="text"
              value={reviewPassword}
              onChange={(e) => setReviewPassword(e.target.value)}
              placeholder="Enter password"
            />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
        )}
        <Button type="submit" disabled={isPending}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t("versions.edit")}
        </Button>
      </div>
    </form>
  );
};
